/* eslint-disable @typescript-eslint/no-unused-vars */
import { Inject, Injectable } from '@angular/core';
import {
  CategoryRepository,
  NonFunctionPropertyNames,
  Product,
  ProductRepository,
  ShopPrice,
  Variant,
  VariantRepository,
  Where
} from '@infrab4a/connect';
import { Papa } from 'ngx-papaparse';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import { UserControllerService } from 'src/app/admin-api';
import {
  getShopProductBatchPublishError,
  getShopProductVariantParentSiblingUpdate,
  parseShopProductPublished,
  pickShopProductParentPersistFields,
  resolveBrandCategoryValue
} from 'src/app/components/shop-product-form/shop-product-form.util';
import { ProductBatchUpdateJSON } from 'src/app/connect-api/models/batch/ProductBatchUpdateJSON';
import { slugify } from 'src/app/utils/slug.util';
import * as xlsx from 'xlsx';
import { BaseConnectService } from '../base-connect.service';
import { ShopLoggingService } from '../shop-log-service';
import { ShopProductGroupService } from '../shop-product-group.service';
import { BatchService } from './batch.service';
import { parseBatchSuppliers } from './parse-batch-suppliers.util';

const CURVE_VALUES = ['A+', 'A', 'B', 'C', 'N'] as const;

const PRODUCT_PUBLISH_FIELDS: NonFunctionPropertyNames<Product>[] = [
  'id',
  'EAN',
  'sku',
  'name',
  'slug',
  'brand',
  'gender',
  'weight',
  'price',
  'metadata',
  'description',
  'images',
  'published',
  'hasVariants',
  'type',
  'NCM',
  'validity',
  'groupId',
  'category'
];

/** Curadoria / supply fields persisted on Connect products but not yet on Product typings. */
type ProductBatchFields = Product & {
  tagsSupply?: string[];
  caixariaMinima?: number | null;
  suppliers?: unknown;
  curve?: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class ProductsBatchService implements BatchService {
  itemsForUpload: Partial<ProductBatchFields>[];
  chunkSize = 100;
  completeCount = 0;
  errors = [];
  validGroups: string[] = [];
  validBrands: Array<{ name?: string }> = [];
  private brandsReady = false;
  importLogProduct = [];

  constructor(
    private papa: Papa,
    @Inject('ProductRepository') private repository: ProductRepository,
    @Inject('VariantRepository') private variantRepository: VariantRepository,
    @Inject('CategoryRepository')
    private categoryRepository: CategoryRepository,
    private messageService: MessageService,
    private productGroupService: ShopProductGroupService,
    private userService: UserControllerService,
    private shopLoggingService: ShopLoggingService
  ) {
    this.loadValidGroups();
  }

  private async loadValidBrands(): Promise<void> {
    try {
      const result = await this.categoryRepository.find({
        fields: ['name'],
        filters: {
          brandCategory: { operator: Where.EQUALS, value: true },
          isWishlist: { operator: Where.EQUALS, value: false }
        },
        limits: { limit: 10000 }
      });
      this.validBrands = (result.data || [])
        .map((category) => ({ name: category.name }))
        .filter((brand) => !!brand.name);
      this.brandsReady = true;
    } catch (error) {
      this.validBrands = [];
      this.brandsReady = false;
    }
  }

  private async loadValidGroups(): Promise<void> {
    try {
      const response = await this.productGroupService.getList();

      this.validGroups = response.map((group) => group.id) || [];
    } catch (error) {
      // Fallback para valores padrão caso ocorra erro
      this.validGroups = [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12'
      ];
    }
  }

  async parse(input: File): Promise<Partial<ProductBatchFields>[]> {
    if (this.validGroups.length === 0) {
      await this.loadValidGroups();
    }
    if (!this.brandsReady) {
      await this.loadValidBrands();
    }

    if (!input) {
      this.itemsForUpload = [];
      return this.itemsForUpload;
    }

    try {
      const rows = await this.readSpreadsheetRows(input);
      this.itemsForUpload = await Promise.all(
        rows
          .filter((c) =>
            Object.keys(c).some((k) => c[k] !== '' && c[k] != null)
          )
          .map(
            async (product: ProductBatchUpdateJSON, index) =>
              await this.convertToProduct(product, index)
          )
      );
      return this.itemsForUpload;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : ((error as { message?: string })?.message ?? String(error));
      throw new Error(`Arquivo inválido: ${message}`);
    }
  }

  async update(): Promise<Array<Partial<Product>>> {
    if (this.itemsForUpload?.length <= 0) {
      return [];
    }

    this.errors = [];

    const chunks = this.getChunkedProductList();
    return await this.startImportation(chunks);
  }

  private async readSpreadsheetRows(
    input: File
  ): Promise<ProductBatchUpdateJSON[]> {
    const name = (input.name || '').toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      const buffer = await input.arrayBuffer();
      const workbook = xlsx.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return xlsx.utils.sheet_to_json<ProductBatchUpdateJSON>(sheet, {
        defval: '',
        raw: true
      });
    }

    return new Promise<ProductBatchUpdateJSON[]>((resolve, reject) => {
      this.papa.parse(input, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        // Auto-detect `,` vs `;` (Excel pt-BR often saves with `;`).
        delimiter: '',
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(results.errors.shift());
            return;
          }
          resolve(results.data as ProductBatchUpdateJSON[]);
        },
        error: (error) => reject(error)
      });
    });
  }

  get importProgress(): number {
    return +((this.completeCount * 100) / this.itemsForUpload.length).toFixed(
      0
    );
  }

  private async convertToProduct(
    product: ProductBatchUpdateJSON,
    index: number
  ): Promise<Partial<ProductBatchFields>> {
    this.validateProduct(product, index);
    const hasAnyMetadata = product.metadataDescription || product.metadataTitle;
    const categoryId: string = await this.searchCategoryReference(
      product.category_reference,
      index
    );
    const curadoria = await this.parseCuradoriaFields(product, index);

    const published = parseShopProductPublished(product.published);

    return Object.entries({
      EAN: product.EAN.toString(),
      price: this.buildProductPrices(product, index),
      ...(published !== undefined && { published }),
      ...(product.description ||
      product.differentials ||
      product.whoMustUse ||
      product.howToUse ||
      product.brandDescription ||
      product.ingredients ||
      product.purpose
        ? {
            description: {
              ...(product.description
                ? { description: product.description }
                : {}),
              ...(product.differentials
                ? { differentials: product.differentials }
                : {}),
              ...(product.whoMustUse ? { whoMustUse: product.whoMustUse } : {}),
              ...(product.howToUse ? { howToUse: product.howToUse } : {}),
              ...(product.brandDescription
                ? { brand: product.brandDescription }
                : {}),
              ...(product.ingredients
                ? { ingredients: product.ingredients }
                : {}),
              ...(product.purpose ? { purpose: product.purpose } : {})
            }
          }
        : {}),
      ...(hasAnyMetadata && {
        metadata: {
          ...(product.metadataDescription
            ? { description: product.metadataDescription }
            : {}),
          ...(product.metadataTitle ? { title: product.metadataTitle } : {})
        }
      }),
      ...(product.type && { type: product.type }),
      ...('tagsCollection' in product && {
        tagsCollection: this.getCollectionTags(product)
      }),
      ...('tagsProfile' in product && {
        tagsProfile: this.getProfileTags(product)
      }),
      ...('tagsSupply' in product && {
        tagsSupply: this.getSupplyTags(product)
      }),
      ...('filters' in product && {
        filters: this.getFilters(product)
      }),
      ...(product.brand && { brand: product.brand.trim() }),
      ...(product.weight && { weight: product.weight }),
      ...(product.name && { name: product.name.trim() }),
      ...(product.name && { slug: slugify(product.name.trim()) }),
      ...(product.NCM && { NCM: product.NCM.toString() }),
      ...(product.sku && { sku: product.sku.toString().trim() }),
      ...(product.CEST && { CEST: product.CEST.toString() }),
      ...(product.stock && { stock: { quantity: product.stock } }),
      ...(product.video && { video: product.video }),
      ...(product.category_reference && { categoryId }),
      ...(product.label && { label: this.getLabel(product.label) }),
      ...(product.group && { groupId: product.group }),
      ...('validity' in product &&
        [true, false].includes(product.validity) && {
          validity: product.validity
        }),
      ...(product.daysOfUse && { daysOfUse: parseInt(product.daysOfUse) }),
      ...curadoria
    }).reduce(
      (iProduct, [key, value]) =>
        ![undefined, null].includes(value)
          ? { ...iProduct, [key]: value }
          : iProduct,
      {}
    );
  }

  private validateProduct(product: ProductBatchUpdateJSON, index: number) {
    if (!product.EAN)
      throw new Error(`Produtos sem campo de EAN na linha ${index + 2}!`);

    const validLabels = [
      'outlet',
      'promocao',
      'ultimasunidades',
      'glamstar',
      'null'
    ];

    if (
      'label' in product &&
      !validLabels.includes(product.label.toLowerCase().trim())
    )
      throw new Error(
        `Label de produto inválido na linha: ${
          index + 2
        }. Os valores permitidos são: outlet, promocao, ultimasunidades, glamstar ou null`
      );

    if (
      'group' in product &&
      !this.validGroups.includes(product.group.toString())
    ) {
      const gruposPermitidos = this.validGroups.join(', ');
      throw new Error(
        `Grupo de produto inválido na linha: ${
          index + 2
        }. Os valores permitidos são: ${gruposPermitidos}`
      );
    }

    if ('validity' in product && ![true, false].includes(product.validity)) {
      throw new Error(
        `Vigência de produto inválida na linha: ${
          index + 2
        }. Os valores permitidos são: true, false`
      );
    }

    if (
      'published' in product &&
      product.published !== '' &&
      product.published != null &&
      parseShopProductPublished(product.published) === undefined
    ) {
      throw new Error(
        `Campo 'published' de produto inválido na linha: ${
          index + 2
        }. Os valores permitidos são: true, false`
      );
    }

    if (
      'category_reference' in product &&
      String(product.category_reference).trim().length < 5
    )
      throw new Error(
        `Apenas categoria de nivel 3 podem ser associada a produtos. Erro na linha: ${
          index + 2
        }`
      );

    if ('daysOfUse' in product && !Number.isInteger(product.daysOfUse))
      throw new Error(
        `Apenas números inteiros podem ser inseridos no campo 'daysOfUse', não é permitido números com casas decimais. Erro na linha: ${
          index + 2
        }`
      );
  }

  private getLabel(input: string) {
    if (input.toLowerCase().trim() == 'promocao') return 'on-sale';
    if (input.toLowerCase().trim() == 'ultimasunidades') return 'last-units';
    if (input.toLowerCase().trim() == 'outlet' || input == 'glamstar')
      return input;
    if (input.toLowerCase().trim() == 'null') return BaseConnectService.NULL;
  }

  private async searchCategoryReference(reference: string, index: number) {
    if (!reference) return null;

    const category = await this.categoryRepository
      .find({
        filters: {
          reference: {
            operator: Where.EQUALS,
            value: reference.toString().trim()
          }
        }
      })
      .then((res) => res.data);

    if (!category.length)
      throw new Error(
        `Categoria do produto não encontrado na linha ${index + 2}!`
      );

    return category.at(0).id.toString();
  }

  private buildProductPrices(
    product: ProductBatchUpdateJSON,
    index: number
  ): ShopPrice {
    if (!(product.price || product.fullPrice || product.discountPercentage)) {
      return;
    }

    if (
      !this.hasValidPrices(
        product.price,
        product.fullPrice,
        product.discountPercentage
      )
    )
      throw new Error(
        `Para atualizar o preços, os campos de price, fullPrice e discountPercentage devem ser informados para o produto na linha ${
          index + 2
        }`
      );

    const hasInvalidCaracters =
      product.price?.toString().includes(',') ||
      product.fullPrice?.toString().includes(',') ||
      product.discountPercentage?.toString().includes(',');

    if (hasInvalidCaracters)
      throw new Error(
        `Produtos com preços fora do padrão na linha ${index + 2}!`
      );

    const subscriberPrice =
      product.price - (product.price * product.discountPercentage) / 100;
    const fullPriceDiscountPercentage =
      100 - (subscriberPrice / product.fullPrice) * 100;

    const prices = {
      ...(product.price && { price: +product.price.toFixed(2) }),
      ...(product.fullPrice && { fullPrice: +product.fullPrice.toFixed(2) }),
      ...(product.discountPercentage && {
        subscriberDiscountPercentage: product.discountPercentage,
        subscriberPrice: +subscriberPrice.toFixed(2),
        fullPriceDiscountPercentage: +fullPriceDiscountPercentage.toFixed(2)
      })
    };

    return prices;
  }

  private hasValidPrices(price, fullPrice, discountPercentage): boolean {
    if (
      (price && (!fullPrice || !discountPercentage)) ||
      (fullPrice && (!price || !discountPercentage)) ||
      (discountPercentage && (!fullPrice || !price))
    )
      return false;

    return true;
  }

  private async startImportation(
    chunks: Partial<Product>[][]
  ): Promise<Array<Partial<Product>>> {
    this.importLogProduct = [];
    let successImportCount = 0;
    this.completeCount = 0;
    const updated: Array<Partial<Product>> = [];
    for (const chunk of chunks) {
      for (const product of chunk) {
        try {
          const p = await this.updateProduct(product);
          if (p) {
            updated.push(p);
            successImportCount++;
          }
        } catch (error) {
          console.log(error);
          this.errors.push(error);
        } finally {
          this.completeCount++;
        }
      }
    }
    if (this.errors.length === this.completeCount) {
      this.messageService.add({
        severity: 'warn',
        detail: `Não foi possível realizar a atualização`,
        summary: 'Erro'
      });
    } else {
      await this.createLog(this.importLogProduct);

      this.messageService.add({
        severity: 'success',
        detail: ` Foram atualizados ${successImportCount} produtos`,
        summary: 'Atualização concluída!'
      });
    }

    return updated?.length ? updated : [];
  }

  private getChunkedProductList(): Partial<Product>[][] {
    const itemsForUpload = [...this.itemsForUpload];

    return [...Array(Math.ceil(itemsForUpload.length / this.chunkSize))].map(
      () => itemsForUpload.splice(0, this.chunkSize)
    );
  }

  private async updateProduct(
    data: Partial<ProductBatchFields>
  ): Promise<Partial<Product>> {
    const updates = [];
    const curadoria = this.pickCuradoriaFields(data);
    const productVariante = await this.variantRepository
      .find({
        fields: ['id', 'EAN', 'sku', 'productId'],
        filters: { EAN: data.EAN },
        limits: { limit: 1 }
      })
      .then((res) => res.data.at(0));
    let productMain = await this.findProductByEan(data.EAN);

    if (!productMain && productVariante?.productId) {
      productMain = await this.findProductById(productVariante.productId);
    }

    this.assertCanPersistPublished(productMain, data);
    this.applyCanonicalBrand(data);
    const persistData = this.toPersistPayload(data);

    if (productVariante) {
      const variant = Object.entries({
        price: persistData.price,
        ...(persistData.sku && { sku: persistData.sku.trim() }),
        ...(persistData.costPrice && { costPrice: persistData.costPrice }),
        ...(persistData.weight && { weight: persistData.weight }),
        ...(persistData.stock && { stock: persistData.stock }),
        ...(persistData.groupId && { groupId: persistData.groupId }),
        ...('tagsCollection' in persistData && {
          tagsCollection: persistData.tagsCollection
        }),
        ...('tagsProfile' in persistData && {
          tagsProfile: persistData.tagsProfile
        }),
        ...('tagsSupply' in persistData && {
          tagsSupply: persistData.tagsSupply
        }),
        ...('daysOfUse' in persistData && { daysOfUse: persistData.daysOfUse }),
        ...('validity' in persistData && { validity: persistData.validity }),
        ...('published' in persistData && { published: persistData.published }),
        ...('filters' in persistData && { filters: persistData.filters }),
        ...('metadata' in persistData && { metadata: persistData.metadata }),
        ...pickShopProductParentPersistFields(persistData),
        ...('label' in persistData && { label: persistData.label }),
        ...('description' in persistData && {
          description: persistData.description
        }),
        ...curadoria
      }).reduce(
        (iProduct, [key, value]) =>
          ![undefined, null].includes(value)
            ? { ...iProduct, [key]: value }
            : iProduct,
        {}
      );

      this.importLogProduct.push(
        Variant.toInstance({
          id: productVariante.id,
          ...variant
        })
      );

      updates.push(
        this.variantRepository.update(
          Variant.toInstance({
            id: productVariante.id,
            productId: productVariante.productId,
            ...variant
          })
        )
      );
    }

    if (productMain && !productVariante) {
      if (productMain.hasVariants) {
        const {
          price,
          tagsProfile,
          tagsCollection,
          tagsSupply,
          groupId,
          filters,
          caixariaMinima,
          curve,
          suppliers,
          ...updateData
        } = persistData;
        updates.push(
          this.repository.update(
            Product.toInstance({
              id: productMain?.id,
              ...updateData
            })
          )
        );

        this.importLogProduct.push(
          Product.toInstance({
            id: productMain?.id,
            ...updateData
          })
        );
      } else {
        updates.push(
          this.repository.update(
            Product.toInstance({
              id: productMain?.id,
              ...persistData
            })
          )
        );

        this.importLogProduct.push(
          Product.toInstance({
            id: productMain?.id,
            ...persistData
          })
        );
      }
    }

    await this.addPublishedSiblingUpdates(
      updates,
      productMain,
      productVariante,
      persistData
    );

    if (!updates.length) {
      throw { message: `EAN ${data.EAN} não encontrado.` };
    }

    await Promise.all(updates);

    return {
      sku: productVariante?.sku ?? productMain?.sku,
      EAN: productVariante?.EAN ?? productMain?.EAN,
      name: productMain.name,
      brand: productMain.brand
    };
  }

  private async findProductByEan(
    ean: string | undefined
  ): Promise<Product | undefined> {
    if (!ean) return undefined;
    return this.repository
      .find({
        fields: PRODUCT_PUBLISH_FIELDS,
        filters: { EAN: ean },
        limits: { limit: 1 }
      })
      .then((res) => res.data.at(0));
  }

  private async findProductById(
    id: string | undefined
  ): Promise<Product | undefined> {
    if (!id) return undefined;
    return this.repository
      .find({
        fields: PRODUCT_PUBLISH_FIELDS,
        filters: { id },
        limits: { limit: 1 }
      })
      .then((res) => res.data.shift());
  }

  private assertCanPersistPublished(
    existing: Product | undefined,
    data: Partial<ProductBatchFields>
  ): void {
    if (!existing) return;
    const error = getShopProductBatchPublishError(
      String(data.EAN),
      this.brandsReady ? this.validBrands : null,
      existing,
      data
    );
    if (error) throw { message: error };
  }

  private applyCanonicalBrand(data: Partial<ProductBatchFields>): void {
    if (!('brand' in data) || data.brand == null || data.brand === '') return;
    const canonical = resolveBrandCategoryValue(this.validBrands, data.brand);
    if (canonical) data.brand = canonical;
  }

  private toPersistPayload(
    data: Partial<ProductBatchFields>
  ): Partial<ProductBatchFields> {
    const { EAN: _ean, ...persistData } = data;
    return persistData;
  }

  private async addPublishedSiblingUpdates(
    updates: Promise<unknown>[],
    productMain: Product | undefined,
    productVariante: Variant | undefined,
    persistData: Partial<ProductBatchFields>
  ): Promise<void> {
    const parentSiblingUpdate =
      getShopProductVariantParentSiblingUpdate(persistData);

    if (productVariante && productMain && Object.keys(parentSiblingUpdate).length) {
      updates.push(
        this.repository.update(
          Product.toInstance({
            id: productMain.id,
            ...parentSiblingUpdate
          })
        )
      );
    }

    if (!('published' in persistData) || persistData.published == null) {
      return;
    }

    if (!productMain?.hasVariants) return;

    const variants = await this.variantRepository
      .find({
        fields: ['id', 'productId'],
        filters: { productId: productMain.id },
        limits: { limit: 1000 }
      })
      .then((res) => res.data || []);

    for (const variant of variants) {
      if (productVariante && variant.id === productVariante.id) continue;
      updates.push(
        this.variantRepository.update(
          Variant.toInstance({
            id: variant.id,
            productId: variant.productId,
            published: persistData.published
          })
        )
      );
    }
  }

  private pickCuradoriaFields(
    data: Partial<ProductBatchFields>
  ): Partial<ProductBatchFields> {
    return {
      ...('caixariaMinima' in data && { caixariaMinima: data.caixariaMinima }),
      ...('suppliers' in data && { suppliers: data.suppliers }),
      ...('curve' in data && { curve: data.curve })
    };
  }

  private async parseCuradoriaFields(
    product: ProductBatchUpdateJSON,
    index: number
  ): Promise<Partial<ProductBatchFields>> {
    const changes: Record<string, unknown> = {};
    const line = index + 2;

    if ('caixariaMinima' in product) {
      const raw = product.caixariaMinima;
      if (raw === '' || raw === null || raw === undefined) {
        changes['caixariaMinima'] = BaseConnectService.NULL;
      } else {
        const value = Number(raw);
        if (!Number.isInteger(value) || value < 0) {
          throw new Error(
            `caixariaMinima inválida na linha ${line}. Informe um inteiro >= 0.`
          );
        }
        changes['caixariaMinima'] = value;
      }
    }

    if ('curve' in product) {
      const raw = product.curve;
      if (raw === '' || raw === null || raw === undefined) {
        changes['curve'] = BaseConnectService.NULL;
      } else {
        const curve = String(raw).trim();
        if (!CURVE_VALUES.includes(curve as (typeof CURVE_VALUES)[number])) {
          throw new Error(
            `curve inválida na linha ${line}. Valores permitidos: ${CURVE_VALUES.join(
              ', '
            )}.`
          );
        }
        changes['curve'] = curve;
      }
    }

    if ('suppliers' in product) {
      try {
        const suppliers = parseBatchSuppliers(product.suppliers);
        changes['suppliers'] =
          suppliers === null ? BaseConnectService.NULL : suppliers;
      } catch (error) {
        const detail =
          error instanceof Error ? error.message : String(error);
        throw new Error(`suppliers inválido na linha ${line}. ${detail}`);
      }
    }

    return changes as Partial<ProductBatchFields>;
  }

  private getCollectionTags(product: ProductBatchUpdateJSON) {
    if ('tagsCollection' in product && product.tagsCollection) {
      const tags = product.tagsCollection
        .toLowerCase()
        .split(';')
        .map((tag) => slugify(tag.trim()))
        .filter((tag) => tag !== 'null')
        .filter(Boolean);

      return [...new Set(tags)];
    }

    if ('tagsCollection' in product && !product.tagsCollection)
      return BaseConnectService.NULL;

    return null;
  }

  private getProfileTags(product: ProductBatchUpdateJSON) {
    if ('tagsProfile' in product && product.tagsProfile) {
      const tags = product.tagsProfile
        .toLowerCase()
        .split(';')
        .map((tag) => slugify(tag.trim()))
        .filter((tag) => tag !== 'null')
        .filter(Boolean);

      return [...new Set(tags)];
    }

    if ('tagsProfile' in product && !product.tagsProfile)
      return BaseConnectService.NULL;

    return null;
  }

  private getSupplyTags(product: ProductBatchUpdateJSON) {
    if ('tagsSupply' in product && product.tagsSupply) {
      const tags = product.tagsSupply
        .toLowerCase()
        .split(';')
        .map((tag) => slugify(tag.trim()))
        .filter((tag) => tag !== 'null')
        .filter(Boolean);

      return [...new Set(tags)];
    }

    if ('tagsSupply' in product && !product.tagsSupply)
      return BaseConnectService.NULL;

    return null;
  }

  private getFilters(product: ProductBatchUpdateJSON) {
    if ('filters' in product && product.filters) {
      const filters = product.filters
        .toLowerCase()
        .split(';')
        .map((filter) => filter.trim())
        .filter((filter) => filter != '');

      return [...new Set(filters)];
    }

    if ('filters' in product && !product.filters)
      return BaseConnectService.NULL;

    return null;
  }

  async createLog(documents: any[]) {
    const user = await lastValueFrom(
      this.userService.getInfo().pipe(map((data) => data.result))
    );

    return this.shopLoggingService.createBatchUpdateLog('products', documents, {
      id: user.userId.toString(),
      name: user.displayName,
      email: user.username
    });
  }
}
